/**
 * Interruptor del diagnostico.
 *
 * POR QUE NO BASTA CON `?diag=1`
 * ------------------------------
 * `main.tsx` daba por hecho que encender el diagnostico en el navegador lo
 * dejaba encendido tambien en la aplicacion instalada, "porque queda en
 * localStorage". **En iOS eso no se cumple**: una app agregada a la pantalla de
 * inicio tiene su PROPIO almacenamiento, separado del de Safari.
 *
 * La consecuencia es peor que un detalle: la sonda del marco existe para medir
 * un defecto que SOLO ocurre en `standalone` —la barra inferior que queda subida
 * con una franja muerta debajo— y en iOS nunca se ha podido encender ahi. Se ha
 * estado midiendo el navegador, que es justo donde el defecto no aparece. Cuatro
 * intentos de arreglo a ciegas se explican solos con eso.
 *
 * Y la PWA no admite parametros: arranca siempre en el `start_url` de su
 * manifiesto.
 *
 * EL GESTO, Y POR QUE ESE
 * -----------------------
 * Cinco toques seguidos en el logotipo. Es el patron de siempre para los ajustes
 * de desarrollo: imposible de encontrar por accidente —nadie toca cinco veces un
 * logotipo— y trivial de explicar por telefono a quien esta en el mostrador.
 *
 * NO ES ANDAMIO TEMPORAL. La sonda del marco se borrara cuando su caso cierre,
 * pero la bitacora de errores (`ErrorLogDiag`) es permanente: es la unica ventana
 * a lo que falla en un aparato que no se puede conectar a un depurador. Sin este
 * gesto, en un iPhone instalado no hay forma de abrirla.
 */

const LLAVE = 'diag';

/** Toques seguidos que encienden o apagan. */
const TOQUES = 5;

/** Cuanto puede tardar entre toque y toque antes de que la cuenta se reinicie. */
const VENTANA_MS = 700;

/**
 * Si el diagnostico esta encendido en ESTE aparato.
 *
 * Se lee una vez al arrancar. `?diag=1` y `?diag=0` siguen funcionando desde el
 * navegador, que es lo comodo cuando hay barra de direcciones.
 */
export function isDiagOn(): boolean {
  const param = new URLSearchParams(window.location.search).get(LLAVE);
  try {
    if (param === '1') localStorage.setItem(LLAVE, '1');
    if (param === '0') localStorage.removeItem(LLAVE);
    return localStorage.getItem(LLAVE) === '1';
  } catch {
    // El modo privado puede negar el almacenamiento; ahi manda la direccion.
    return param === '1';
  }
}

/**
 * Enciende o apaga, y recarga.
 *
 * La bandera se lee UNA vez al evaluar `main.tsx`, asi que cambiarla sin
 * recargar no dibuja ni quita nada — y quien lo intentara concluiria que el
 * gesto no sirve.
 */
function alternar(): void {
  try {
    const estaba = localStorage.getItem(LLAVE) === '1';
    if (estaba) localStorage.removeItem(LLAVE);
    else localStorage.setItem(LLAVE, '1');
  } catch {
    return;
  }
  window.location.reload();
}

/**
 * Cuenta los toques y alterna al llegar a cinco.
 *
 * Devuelve el manejador para colgarlo de donde sea. No usa estado de React a
 * proposito: no hay nada que redibujar hasta la recarga, y un `useState` aqui
 * volveria a pintar el encabezado con cada toque.
 */
export function makeDiagTapHandler(): () => void {
  let cuenta = 0;
  let ultimo = 0;
  return () => {
    const ahora = Date.now();
    cuenta = ahora - ultimo > VENTANA_MS ? 1 : cuenta + 1;
    ultimo = ahora;
    if (cuenta >= TOQUES) {
      cuenta = 0;
      alternar();
    }
  };
}
